import { Switch } from '@mui/material';
import MyEditModal from 'components/MyEditModal/MyEditModal';
import MyMoneyInput from 'components/MyMoneyInput/MyMoneyInput';
import PropertyContainer from 'components/PropertyContainer/PropertyContainer';
import PropertyDisplay from 'components/PropertyDisplay/PropertyDisplay';
import PropertyEditDate from 'components/PropertyEditDate/PropertyEditDate';
import PropertyEditSelect from 'components/PropertyEditSelect/PropertyEditSelect';
import PropertyEditText from 'components/PropertyEditText/PropertyEditText';
import SalesOrderCoreStatus from 'features/sales/enums/SalesOrderCoreStatus';
import { useSalesOrderStatusOptions } from 'features/sales/hooks/useSalesOrderStatusOptions';
import { SalesOrder } from 'features/sales/models/SalesOrder';
import salesApi from 'features/sales/sales.api';
import React, { useCallback, useMemo } from 'react';
import coalesceClassNames from 'utils/coalesceClassNames';
import { formatCurrency } from 'utils/helpers';
import './SalesOrderEditModal.scss';

export default function SalesOrderEditModal({
    model,
    close,
}: {
    model?: SalesOrder;
    close?: () => void;
}) {
    const statusOptions = useSalesOrderStatusOptions({
        currentId: model?.orderStatusId,
    });
    const [updateMutation, updateMutationStatus] = salesApi.useSalesOrderUpdateMutation();

    const handleSave = useCallback(
        async (editModel: SalesOrder) => {
            await updateMutation(editModel);
            close?.();
        },
        [close, updateMutation],
    );

    // before the order is cancelled we only show uncancelled statuses
    // after it has been cancelled we only show cancelled statuses
    const notCancelledStatusOptions = useMemo(() => {
        return statusOptions?.filter(o => o.coreStatus !== SalesOrderCoreStatus.Cancelled);
    }, [statusOptions]);

    const cancelledStatusOptions = useMemo(() => {
        return statusOptions?.filter(o => o.coreStatus === SalesOrderCoreStatus.Cancelled);
    }, [statusOptions]);

    const isAlreadyCancelled = useMemo(
        () => cancelledStatusOptions?.find(o => o.value === `${model?.orderStatusId}`),
        [cancelledStatusOptions, model?.orderStatusId],
    );

    const isSaving = updateMutationStatus.isLoading;
    return (
        <MyEditModal
            className="SalesOrderEditModal"
            title="Edit Sales Order"
            titleContext={`${model?.manufacturerReference}`}
            close={close}
            isLoading={!statusOptions}
            // isError={isError}
            model={model}
            onSave={handleSave}
            isSaving={isSaving}
            readonly={false}
            fullHeight={false}
            editImmediately={true}
            containerSelector="#modal-root-top"
        >
            {({ editModel, isEditing, updateField }) => {
                const isFreightOverridden = typeof editModel?.totalFreightOverride === 'number';
                return (
                    <PropertyContainer
                        className="SalesOrderEditModal__PropertyContainer"
                        layout="table"
                    >
                        <PropertyEditSelect
                            label="Status"
                            value={`${editModel.orderStatusId}`}
                            onChange={(val: string) => {
                                const orderStatusId = parseInt(val, 10);
                                updateField({ orderStatusId });
                            }}
                            readonly={!isEditing}
                            options={
                                isAlreadyCancelled
                                    ? cancelledStatusOptions
                                    : notCancelledStatusOptions
                            }
                            disabled={isSaving}
                            validationRequired={true}
                        />

                        <PropertyEditDate
                            label="ETA"
                            value={editModel.eta ?? ''}
                            onChange={val => updateField({ eta: val || null })}
                            readonly={!isEditing}
                            disabled={isSaving}
                        />

                        <div className="PropertyContainerSpacer" />

                        <PropertyDisplay
                            label="Total freight"
                            value={
                                <span
                                    className={coalesceClassNames(
                                        'SalesOrderEditModal__TotalFreight',
                                        isFreightOverridden &&
                                            'SalesOrderEditModal__TotalFreight--overridden',
                                    )}
                                >
                                    {formatCurrency(editModel.totalFreight)}
                                </span>
                            }
                        />

                        <PropertyDisplay
                            label="Override total freight"
                            value={
                                <div className="SalesOrderEditModal__FreightOverride">
                                    <Switch
                                        checked={isFreightOverridden}
                                        onChange={e => {
                                            const totalFreightOverride = e.target.checked
                                                ? 0
                                                : null;
                                            updateField({
                                                totalFreightOverride,
                                            });
                                        }}
                                    />
                                    {isFreightOverridden && (
                                        <MyMoneyInput
                                            value={
                                                editModel?.totalFreightOverride ??
                                                editModel?.totalFreight
                                            }
                                            handleInput={val => {
                                                if (val !== undefined) {
                                                    updateField({
                                                        totalFreightOverride: val,
                                                    });
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            }
                        />

                        <div className="PropertyContainerSpacer" />

                        <PropertyEditText
                            label="Tracking information"
                            value={editModel.trackingInformation ?? ''}
                            onChange={val => updateField({ trackingInformation: val })}
                        />

                        <PropertyEditText
                            label="Internal Notes"
                            hint="Internal only"
                            value={editModel.notes ?? ''}
                            onChange={val => updateField({ notes: val })}
                            multiline={true}
                            readonly={!isEditing}
                            disabled={isSaving}
                        />

                        <PropertyEditText
                            label="External notes"
                            hint="Visible to customer"
                            value={editModel.customerNotes ?? ''}
                            onChange={val => updateField({ customerNotes: val })}
                            multiline={true}
                            readonly={!isEditing}
                            disabled={isSaving}
                        />

                        {/* customer_notes: order.customerNotes,
                    notes: order.notes,
                    order_status_id: order.statusId,
                    total_freight_override: order.totalFreightOverride,
                    tracking_information: order.trackingInformation, */}
                    </PropertyContainer>
                );
            }}
        </MyEditModal>
    );
}
